import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, Mail, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import logoNav from "@/assets/logo-nav.webp";

const destinationLinks: { label: string; path: string | null }[] = [
  { label: "Ibiza", path: "/ibiza" },
  { label: "Marbella", path: "/marbella" },
  { label: "Saint-Tropez", path: "/saint-tropez" },
  { label: "Dubai", path: "/dubai" },
  { label: "Paris", path: "/paris" },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [mobileDestOpen, setMobileDestOpen] = useState(false);
  const destRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      setPastHero(window.scrollY > window.innerHeight * 0.8);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (destRef.current && !destRef.current.contains(e.target as Node)) {
        setDestOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    setDestOpen(false);
    if (!isHome) {
      navigate("/#" + id);
      return;
    }
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth" });
  };

  const textColor = pastHero ? "text-foreground" : "text-primary-foreground";

  const navItems: { label: string; id?: string; path?: string }[] = [
    { label: t("Residences", "Residenties"), path: "/residences" },
    { label: "Drive", path: "/drive" },
    { label: t("Yachts", "Jachten"), path: "/yachts" },
    { label: "Lifestyle", path: "/lifestyle" },
    { label: t("About", "Over Ons"), path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 aman-ease ${
          pastHero
            ? "backdrop-blur-xl bg-background/90"
            : scrolled
            ? "backdrop-blur-md bg-background/30"
            : "bg-transparent"
        }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Desktop: top row with logo centered + socials right */}
        <div className="container-luxury hidden lg:flex items-center justify-between h-20">
          <div className="w-32 flex items-center">
            <button
              onClick={() => setLanguage(language === "en" ? "nl" : "en")}
              className={`font-label text-[10px] tracking-[0.15em] transition-colors duration-700 ${textColor} hover:opacity-60 border border-current/20 px-2 py-1`}
            >
              {language === "en" ? "NL" : "EN"}
            </button>
          </div>
          <button onClick={() => { if (!isHome) navigate("/"); else window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <img
              src={logoNav}
              alt="Arzani Group"
              className={`h-14 md:h-16 transition-all duration-700 ${pastHero ? "invert" : ""}`}
            />
          </button>
          <div className="flex items-center gap-4 w-32 justify-end">
            <a href="https://www.instagram.com/arzanigroup" target="_blank" rel="noopener noreferrer" aria-label="Arzani Group on Instagram" className={`transition-colors duration-700 ${textColor} hover:opacity-60`}>
              <Instagram size={16} />
            </a>
            <a href="https://wa.me/message/2XEKJ5STR2IZJ1" target="_blank" rel="noopener noreferrer" aria-label="Contact Arzani Group on WhatsApp" className={`transition-colors duration-700 ${textColor} hover:opacity-60`}>
              <MessageCircle size={16} />
            </a>
            <a href="mailto:info@arzanigroupworldwide.com" aria-label="Email Arzani Group" className={`transition-colors duration-700 ${textColor} hover:opacity-60`}>
              <Mail size={16} />
            </a>
          </div>
        </div>

        {/* Desktop: bottom row with nav links */}
        <div className="container-luxury hidden lg:flex items-center justify-center gap-10 h-10 border-t border-primary-foreground/10">
          <div ref={destRef} className="relative">
            <button
              onClick={() => setDestOpen(!destOpen)}
              className={`font-label flex items-center gap-1 transition-colors duration-700 ${textColor} hover:opacity-60`}
            >
              {t("Destinations", "Bestemmingen")}
              <ChevronDown size={10} className={`transition-transform duration-300 ${destOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {destOpen && (
                <motion.div
                  className="absolute top-full left-0 mt-3 bg-background/95 backdrop-blur-lg border border-border py-3 px-5 min-w-[180px] shadow-lg"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  {destinationLinks.map((dest) => (
                    <button
                      key={dest.label}
                      onClick={() => { setDestOpen(false); dest.path ? navigate(dest.path) : scrollTo("destinations"); }}
                      className="block w-full text-left font-label text-foreground hover:text-bronze transition-colors duration-500 py-2"
                    >
                      {dest.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => item.path ? navigate(item.path) : scrollTo(item.id!)}
              className={`font-label transition-colors duration-700 ${textColor} hover:opacity-60`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Mobile nav bar */}
        <div className="container-luxury flex lg:hidden items-center justify-between h-20">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`font-label transition-colors duration-700 ${textColor} hover:opacity-60`}
          >
            {menuOpen ? t("Close", "Sluiten") : "Menu"}
          </button>

          <div className="absolute left-1/2 -translate-x-1/2">
            <img
              src={logoNav}
              alt="Arzani Group"
              className={`h-14 md:h-20 transition-all duration-700 ${pastHero ? "invert" : ""}`}
            />
          </div>

          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/arzanigroup" target="_blank" rel="noopener noreferrer" aria-label="Arzani Group on Instagram" className={`transition-colors duration-700 ${textColor} hover:opacity-60`}>
              <Instagram size={18} />
            </a>
            <a href="https://wa.me/message/2XEKJ5STR2IZJ1" target="_blank" rel="noopener noreferrer" aria-label="Contact Arzani Group on WhatsApp" className={`transition-colors duration-700 ${textColor} hover:opacity-60`}>
              <MessageCircle size={18} />
            </a>
            <a href="mailto:info@arzanigroupworldwide.com" aria-label="Email Arzani Group" className={`transition-colors duration-700 ${textColor} hover:opacity-60`}>
              <Mail size={18} />
            </a>
          </div>
        </div>
      </motion.header>

      {/* Mobile full-screen menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-warm-black flex flex-col items-center justify-center lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Language toggle in mobile menu */}
            <button
              onClick={() => setLanguage(language === "en" ? "nl" : "en")}
              className="absolute top-8 right-8 font-label text-primary-foreground/60 text-sm tracking-[0.15em] border border-primary-foreground/20 px-3 py-1.5 hover:text-primary-foreground hover:border-primary-foreground/40 transition-colors duration-500"
            >
              {language === "en" ? "NL" : "EN"}
            </button>

            <nav className="flex flex-col items-center gap-6">
              <motion.button
                onClick={() => { setMenuOpen(false); navigate("/"); }}
                className="font-display text-4xl md:text-6xl text-primary-foreground hover:text-bronze transition-colors duration-700"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                Home
              </motion.button>

              {/* Destinations with expandable submenu */}
              <motion.div
                className="flex flex-col items-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.23, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <button
                  onClick={() => setMobileDestOpen(!mobileDestOpen)}
                  className="font-display text-4xl md:text-6xl text-primary-foreground hover:text-bronze transition-colors duration-700 flex items-center gap-2"
                >
                  {t("Destinations", "Bestemmingen")}
                  <ChevronDown size={20} className={`transition-transform duration-300 ${mobileDestOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileDestOpen && (
                    <motion.div
                      className="flex flex-col items-center gap-3 mt-4"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {destinationLinks.map((dest) => (
                        <button
                          key={dest.label}
                          onClick={() => { setMenuOpen(false); setMobileDestOpen(false); dest.path ? navigate(dest.path) : scrollTo("destinations"); }}
                          className="font-label text-xl text-primary-foreground/70 hover:text-bronze transition-colors duration-500 tracking-[0.15em]"
                        >
                          {dest.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {[
                { label: t("Residences", "Residenties"), path: "/residences" },
                { label: "Drive", path: "/drive" },
                { label: t("Yachts", "Jachten"), path: "/yachts" },
                { label: "Lifestyle", path: "/lifestyle" },
                { label: t("About", "Over Ons"), path: "/about" },
                { label: "Contact", path: "/contact" },
              ].map((item, i) => (
                <motion.button
                  key={item.label}
                  onClick={() => { setMenuOpen(false); navigate(item.path); }}
                  className="font-display text-4xl md:text-6xl text-primary-foreground hover:text-bronze transition-colors duration-700"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.31 + i * 0.08, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                >
                  {item.label}
                </motion.button>
              ))}
            </nav>

            <motion.div
              className="absolute bottom-12 flex items-center gap-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
            >
              <a href="https://www.instagram.com/arzanigroup" target="_blank" rel="noopener noreferrer" className="font-label text-primary-foreground/40 hover:text-primary-foreground/80 transition-colors duration-500">
                Instagram
              </a>
              <a href="https://wa.me/message/2XEKJ5STR2IZJ1" target="_blank" rel="noopener noreferrer" className="font-label text-primary-foreground/40 hover:text-primary-foreground/80 transition-colors duration-500">
                WhatsApp
              </a>
              <a href="mailto:info@arzanigroupworldwide.com" className="font-label text-primary-foreground/40 hover:text-primary-foreground/80 transition-colors duration-500">
                Email
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
